/**
 * SoleadLab 2.0 - Data Point Config Modal
 *
 * Port of showDataPointConfigModal() from editor (23).html (lines 17359–17477).
 * Sections: Entity Type → Entity → Label → Metrics checkbox grid →
 *           Alarm Rules + Compound Groups → Coordinates → Save/Cancel
 */

import { useState, useMemo } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { useMonitoringStore, useUIStore } from '@/stores';
import type {
  DataPointConfig,
  AlarmRule,
  AlarmRuleGroup,
  AlarmCondition,
  AlarmOperator,
  AlarmSeverity,
  DatapointGroup,
  SchemaEntity,
} from '@/types/monitoring';
import './DataPointConfigModal.css';

interface DataPointConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  datapointId?: string; // If editing existing
  initialCoordinates?: [number, number]; // If placing on map
}

const OPERATORS: { value: AlarmOperator; label: string }[] = [
  { value: '>', label: '>' },
  { value: '>=', label: '>=' },
  { value: '<', label: '<' },
  { value: '<=', label: '<=' },
  { value: '==', label: '==' },
  { value: '!=', label: '!=' },
];

const GROUP_OPTIONS: { value: DatapointGroup; label: string }[] = [
  { value: 'inverters', label: 'Inverters' },
  { value: 'meters', label: 'Meters' },
  { value: 'sensors', label: 'Sensors' },
  { value: 'statuses', label: 'Statuses' },
  { value: 'ppcs', label: 'PPCs' },
  { value: 'stringboxes', label: 'Stringboxes' },
  { value: 'batteries', label: 'Batteries' },
  { value: 'tracker', label: 'Trackers' },
  { value: 'basics', label: 'Basics' },
  { value: 'other', label: 'Other' },
];

export default function DataPointConfigModal({
  isOpen,
  onClose,
  datapointId,
  initialCoordinates,
}: DataPointConfigModalProps) {
  const projectConfig = useMonitoringStore((s) => s.projectConfig);
  const schema = useMonitoringStore((s) => s.schema);
  const addDataPointConfig = useMonitoringStore((s) => s.addDataPointConfig);
  const updateDataPointConfig = useMonitoringStore((s) => s.updateDataPointConfig);
  const addNotification = useUIStore((s) => s.addNotification);

  const isEdit = !!datapointId;
  const existingConfig = isEdit
    ? projectConfig?.datapoints.find((dp) => dp.id === datapointId)
    : null;

  // ── Form State ──
  const [entityType, setEntityType] = useState<string>(existingConfig?.group ?? 'other');
  const [entityId, setEntityId] = useState(existingConfig?.entity_id ?? '');
  const [displayName, setDisplayName] = useState(existingConfig?.display_name ?? '');
  const [unit, setUnit] = useState(existingConfig?.unit ?? '');
  const [decimals, setDecimals] = useState(existingConfig?.decimals ?? 2);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(
    existingConfig?.selected_metrics ?? []
  );
  const [metricUnits, setMetricUnits] = useState<Record<string, string>>(existingConfig?.metric_units ?? {});
  const [alarmRules, setAlarmRules] = useState<AlarmRule[]>(existingConfig?.rules ?? []);
  const [ruleGroups, setRuleGroups] = useState<AlarmRuleGroup[]>(existingConfig?.rule_groups ?? []);

  // ── Schema-derived Data ──
  const entityTypes = useMemo(() => {
    if (!schema?.entities) return [];
    const types = [...new Set(schema.entities.map((e) => e.entity_type))];
    return types.sort();
  }, [schema]);

  const entitiesForType = useMemo(() => {
    if (!schema?.entities || !entityType) return [];
    return schema.entities.filter((e) => e.entity_type === entityType);
  }, [schema, entityType]);

  const availableMetrics = useMemo(() => {
    const entity = schema?.entities.find((e) => e.entity_id === entityId);
    return entity?.metrics ?? [];
  }, [schema, entityId]);

  // ── Handlers ──
  const handleEntityTypeChange = (type: string) => {
    setEntityType(type);
    setEntityId('');
    setSelectedMetrics([]);
  };

  const handleEntityChange = (id: string) => {
    setEntityId(id);
    setSelectedMetrics([]);
    const entity = schema?.entities.find((e) => e.entity_id === id);
    if (entity && !displayName) {
      setDisplayName(id);
    }
  };

  const toggleMetric = (key: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]
    );
  };

  const selectAllMetrics = () => {
    setSelectedMetrics(availableMetrics.map((m) => m.key));
  };

  const selectNoMetrics = () => {
    setSelectedMetrics([]);
  };

  // ── Alarm Rule Handlers ──
  const addRule = () => {
    const newRule: AlarmRule = {
      id: crypto.randomUUID(),
      enabled: true,
      metric: availableMetrics[0]?.key ?? '',
      op: '>',
      value: 0,
      severity: 'warning',
    };
    setAlarmRules((prev) => [...prev, newRule]);
  };

  const updateRule = (index: number, updates: Partial<AlarmRule>) => {
    setAlarmRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...updates } : r))
    );
  };

  const removeRule = (index: number) => {
    setAlarmRules((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Rule Group Handlers ──
  const addRuleGroup = () => {
    const newGroup: AlarmRuleGroup = {
      id: crypto.randomUUID(),
      combinator: 'AND',
      conditions: [
        { metric: availableMetrics[0]?.key ?? '', op: '>', value: 0 },
      ],
      severity: 'warning',
      enabled: true,
    };
    setRuleGroups((prev) => [...prev, newGroup]);
  };

  const updateRuleGroup = (index: number, updates: Partial<AlarmRuleGroup>) => {
    setRuleGroups((prev) =>
      prev.map((g, i) => (i === index ? { ...g, ...updates } : g))
    );
  };

  const removeRuleGroup = (index: number) => {
    setRuleGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const addConditionToGroup = (groupIndex: number) => {
    setRuleGroups((prev) =>
      prev.map((g, i) =>
        i === groupIndex
          ? {
              ...g,
              conditions: [
                ...g.conditions,
                { metric: availableMetrics[0]?.key ?? '', op: '>' as AlarmOperator, value: 0 },
              ],
            }
          : g
      )
    );
  };

  const updateCondition = (
    groupIndex: number,
    condIndex: number,
    updates: Partial<AlarmCondition>
  ) => {
    setRuleGroups((prev) =>
      prev.map((g, i) =>
        i === groupIndex
          ? {
              ...g,
              conditions: g.conditions.map((c, ci) =>
                ci === condIndex ? { ...c, ...updates } : c
              ),
            }
          : g
      )
    );
  };

  const removeCondition = (groupIndex: number, condIndex: number) => {
    setRuleGroups((prev) =>
      prev.map((g, i) =>
        i === groupIndex
          ? { ...g, conditions: g.conditions.filter((_, ci) => ci !== condIndex) }
          : g
      )
    );
  };

  // ── Save ──
  const handleSave = async () => {
    if (!entityId || !displayName) return;

    const config: DataPointConfig = {
      id: existingConfig?.id ?? crypto.randomUUID(),
      group: entityType as DatapointGroup,
      display_name: displayName,
      entity_id: entityId,
      enabled: true,
      unit: unit || undefined,
      metric_units: Object.keys(metricUnits).filter(k => metricUnits[k]).length > 0
        ? Object.fromEntries(Object.entries(metricUnits).filter(([, v]) => v))
        : undefined,
      decimals,
      poll_interval_seconds: 60,
      selected_metrics: selectedMetrics,
      available_metrics: availableMetrics,
      rules: alarmRules,
      rule_groups: ruleGroups,
      coordinates: initialCoordinates ?? existingConfig?.coordinates,
      provider: existingConfig?.provider,
    };

    if (isEdit) {
      // B2 interim (B3b): existing-datapoint edit (bundled metadata + nested rules/groups)
      // stays local-mutate + Publish until the B3b diff-sync decomposition.
      updateDataPointConfig(config.id, config);
      onClose();
    } else {
      // B3a — a NEW datapoint persists immediately via the canonical POST. Keep the modal open
      // on failure so the user can retry; the conflict/error message comes from the store.
      const ok = await addDataPointConfig(config);
      if (ok) onClose();
      else addNotification({ type: 'error', title: 'Could not add data point', message: useMonitoringStore.getState().configError ?? 'Please retry.' });
    }
  };

  // ── Render ──
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Data Point' : 'New Data Point'}
      size="md"
      footer={
        <div className="dpc-footer">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!entityId || !displayName}
          >
            {isEdit ? 'Update' : 'Save Point'}
          </Button>
        </div>
      }
    >
      <div className="dpc-form">
        {/* Entity Type */}
        <div className="dpc-field">
          <label className="dpc-label">Entity Type</label>
          <select
            className="dpc-select"
            value={entityType}
            onChange={(e) => handleEntityTypeChange(e.target.value)}
          >
            <option value="">Select type...</option>
            {entityTypes.length > 0
              ? entityTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))
              : GROUP_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))
            }
          </select>
        </div>

        {/* Entity */}
        <div className="dpc-field">
          <label className="dpc-label">Entity</label>
          {entitiesForType.length > 0 ? (
            <select
              className="dpc-select"
              value={entityId}
              onChange={(e) => handleEntityChange(e.target.value)}
            >
              <option value="">Select entity...</option>
              {entitiesForType.map((entity: SchemaEntity) => (
                <option key={entity.entity_id} value={entity.entity_id}>
                  {entity.entity_id}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="dpc-input"
              type="text"
              placeholder="Entity ID (e.g., INV_001)"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            />
          )}
        </div>

        {/* Label */}
        <div className="dpc-field">
          <label className="dpc-label">Display Name</label>
          <input
            className="dpc-input"
            type="text"
            placeholder="Custom display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        {/* Unit & Decimals */}
        <div className="dpc-row">
          <div className="dpc-field dpc-field--half">
            <label className="dpc-label">Unit</label>
            <input
              className="dpc-input"
              type="text"
              placeholder="kW, V, A..."
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </div>
          <div className="dpc-field dpc-field--half">
            <label className="dpc-label">Decimals</label>
            <input
              className="dpc-input"
              type="number"
              min={0}
              max={6}
              value={decimals}
              onChange={(e) => setDecimals(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>

        {/* Display Metrics */}
        {availableMetrics.length > 0 && (
          <div className="dpc-field">
            <div className="dpc-label-row">
              <label className="dpc-label">Display Metrics</label>
              <div className="dpc-label-actions">
                <button className="dpc-link-btn" onClick={selectAllMetrics}>All</button>
                <button className="dpc-link-btn" onClick={selectNoMetrics}>None</button>
              </div>
            </div>
            <div className="dpc-metrics-grid">
              {availableMetrics.map((m) => {
                const isChecked = selectedMetrics.includes(m.key);
                return (
                  <div
                    key={m.key}
                    className={`dpc-metric-item ${isChecked ? 'dpc-metric-item--checked' : ''}`}
                  >
                    <label className="dpc-metric-label">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleMetric(m.key)}
                      />
                      <span>{m.label || m.key}</span>
                    </label>
                    {isChecked && (
                      <input
                        className="dpc-metric-unit"
                        type="text"
                        placeholder={unit || 'unit'}
                        value={metricUnits[m.key] ?? ''}
                        onChange={(e) => setMetricUnits(prev => ({ ...prev, [m.key]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Alarm Rules */}
        <div className="dpc-section">
          <div className="dpc-section-header">
            <span className="dpc-section-title">Alarm Rules</span>
            <button className="dpc-link-btn" onClick={addRule}>+ Add Rule</button>
          </div>

          {alarmRules.length === 0 && ruleGroups.length === 0 && (
            <p className="dpc-empty">No alarm rules. Click "+ Add Rule" to create one.</p>
          )}

          {alarmRules.map((rule, i) => (
            <div key={rule.id} className="dpc-rule-row">
              <select
                className="dpc-select dpc-select--sm"
                value={rule.metric}
                onChange={(e) => updateRule(i, { metric: e.target.value })}
              >
                {(availableMetrics.length > 0 ? availableMetrics : selectedMetrics.map((k) => ({ key: k }))).map((m) => (
                  <option key={m.key} value={m.key}>{m.key}</option>
                ))}
              </select>
              <select
                className="dpc-select dpc-select--xs"
                value={rule.op}
                onChange={(e) => updateRule(i, { op: e.target.value as AlarmOperator })}
              >
                {OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
              <input
                className="dpc-input dpc-input--xs"
                type="number"
                value={rule.value}
                onChange={(e) => updateRule(i, { value: parseFloat(e.target.value) || 0 })}
              />
              <select
                className="dpc-select dpc-select--sm"
                value={rule.severity}
                onChange={(e) => updateRule(i, { severity: e.target.value as AlarmSeverity })}
              >
                <option value="warning">WARNING</option>
                <option value="critical">CRITICAL</option>
              </select>
              <button className="dpc-rule-delete" onClick={() => removeRule(i)} title="Remove rule">
                &times;
              </button>
            </div>
          ))}
        </div>

        {/* Compound Rule Groups */}
        <div className="dpc-section">
          <div className="dpc-section-header">
            <span className="dpc-section-title">Compound Rules (AND/OR)</span>
            <button className="dpc-link-btn" onClick={addRuleGroup}>+ Add Group</button>
          </div>

          {ruleGroups.map((group, gi) => (
            <div key={group.id} className="dpc-rule-group">
              <div className="dpc-rule-group-header">
                <select
                  className="dpc-select dpc-select--sm"
                  value={group.combinator}
                  onChange={(e) =>
                    updateRuleGroup(gi, { combinator: e.target.value as 'AND' | 'OR' })
                  }
                >
                  <option value="AND">AND (all must match)</option>
                  <option value="OR">OR (any matches)</option>
                </select>
                <select
                  className="dpc-select dpc-select--sm"
                  value={group.severity}
                  onChange={(e) =>
                    updateRuleGroup(gi, { severity: e.target.value as AlarmSeverity })
                  }
                >
                  <option value="warning">WARNING</option>
                  <option value="critical">CRITICAL</option>
                </select>
                <button
                  className="dpc-rule-delete"
                  onClick={() => removeRuleGroup(gi)}
                  title="Remove group"
                >
                  &times;
                </button>
              </div>

              {group.conditions.map((cond, ci) => (
                <div key={ci} className="dpc-rule-row dpc-rule-row--nested">
                  <select
                    className="dpc-select dpc-select--sm"
                    value={cond.metric}
                    onChange={(e) => updateCondition(gi, ci, { metric: e.target.value })}
                  >
                    {(availableMetrics.length > 0 ? availableMetrics : selectedMetrics.map((k) => ({ key: k }))).map((m) => (
                      <option key={m.key} value={m.key}>{m.key}</option>
                    ))}
                  </select>
                  <select
                    className="dpc-select dpc-select--xs"
                    value={cond.op}
                    onChange={(e) => updateCondition(gi, ci, { op: e.target.value as AlarmOperator })}
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    className="dpc-input dpc-input--xs"
                    type="number"
                    value={cond.value}
                    onChange={(e) =>
                      updateCondition(gi, ci, { value: parseFloat(e.target.value) || 0 })
                    }
                  />
                  {group.conditions.length > 1 && (
                    <button
                      className="dpc-rule-delete"
                      onClick={() => removeCondition(gi, ci)}
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}

              <button
                className="dpc-link-btn dpc-link-btn--indent"
                onClick={() => addConditionToGroup(gi)}
              >
                + Add Condition
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
